import { Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, Output, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import Hls from 'hls.js';
import { HttpService } from '../shared/services/http.service';
import { BunnyMapService } from '../shared/services/bunny-map.service';

@Component({
    selector: 'app-video-detail',
    templateUrl: './video-detail.component.html',
    styleUrls: ['./video-detail.component.scss'],
    standalone: false
})
export class VideoDetailComponent implements OnChanges, OnDestroy {

  @ViewChild('videoEl', { static: true }) videoElRef!: ElementRef<HTMLVideoElement>;

  @Input() video: any;
  @Output() close: EventEmitter<boolean> = new EventEmitter<boolean>();

  submitted = false
  saveState: 'idle' | 'saving' | 'success' = 'idle'
  errorMessage = ''
  isNew = true

  videoForm: UntypedFormGroup

  vimeoPreviewUrl: SafeResourceUrl | null = null

  private hls: Hls | null = null;

  constructor(
    private fb: UntypedFormBuilder,
    private httpService: HttpService,
    private bunnyMapService: BunnyMapService,
    private sanitizer: DomSanitizer
  ) {
    this.createVideoForm()
  }

  get f() {
    return this.videoForm.controls
  }

  ngOnChanges() {
    this.isNew = !this.video?.id
    this.rebuildForm()
    this.loadPreview()
    this.loadVimeoPreview()
  }

  ngOnDestroy() {
    this.hls?.destroy()
  }

  private loadPreview() {
    const video = this.videoElRef.nativeElement;
    const bunnyId = this.video?.bunny_id;

    this.hls?.destroy();
    this.hls = null;
    Array.from(video.querySelectorAll('track')).forEach(t => t.remove());
    video.removeAttribute('src');
    video.load();

    if (!bunnyId) {
      return;
    }

    const src = this.bunnyMapService.getPlaylistUrl(bunnyId);

    if (Hls.isSupported()) {
      this.hls = new Hls();
      this.hls.loadSource(src);
      this.hls.attachMedia(video);
    } else {
      video.src = src;
    }

    const captionTrack = document.createElement('track');
    captionTrack.kind = 'captions';
    captionTrack.label = 'English';
    captionTrack.srclang = 'GB';
    captionTrack.default = true;
    captionTrack.src = this.bunnyMapService.getCaptionsUrl(bunnyId);
    captionTrack.addEventListener('error', () => {
      captionTrack.src = this.bunnyMapService.getCaptionsUrl(bunnyId, 'EN');
      captionTrack.track.mode = 'showing';
    }, { once: true });
    video.appendChild(captionTrack);
    captionTrack.track.mode = 'showing';
  }

  private loadVimeoPreview() {
    const vimeoId = this.video?.id;

    if (!vimeoId) {
      this.vimeoPreviewUrl = null;
      return;
    }

    const src = `https://player.vimeo.com/video/${vimeoId}?texttrack=en`;
    this.vimeoPreviewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(src);
  }

  createVideoForm() {
    this.videoForm = this.fb.group({
      id: ['', Validators.required],
      bunny_id: ['', Validators.required],
      title: ['', Validators.required],
      thumbnail: [''],
      duration: [''],
    })
  }

  rebuildForm() {
    this.videoForm.reset({
      id: this.video?.id ?? '',
      bunny_id: this.video?.bunny_id ?? '',
      title: this.video?.title ?? '',
      thumbnail: this.video?.thumbnail ?? '',
      duration: this.video?.duration ?? '',
    })

    this.submitted = false
    this.saveState = 'idle'
    this.errorMessage = ''
  }

  onCancel() {
    this.close.emit(false)
  }

  async onSubmit() {
    this.submitted = true

    if (this.videoForm.invalid) {
      return
    }

    this.errorMessage = ''
    this.saveState = 'saving'

    const formModel = this.videoForm.value

    try {
      const result: any = await this.httpService.postAsync('hcontent/saveVideo', {
        id: formModel.id.trim(),
        bunnyId: formModel.bunny_id.trim(),
        title: formModel.title.trim(),
        thumbnail: formModel.thumbnail ? formModel.thumbnail.trim() : '',
        duration: formModel.duration ? formModel.duration.trim() : '',
        isNew: this.isNew,
      })

      if (!result?.success) {
        throw new Error(result?.message || 'Failed to save video.')
      }

      this.saveState = 'success'
      setTimeout(() => {
        this.close.emit(true)
      }, 300)
    } catch (e: any) {
      this.saveState = 'idle'
      this.errorMessage = e?.message || 'Failed to save video.'
    }
  }
}
